import { DoublyLinkedList } from './doubly-linked-list.js'

/**
 * Cola de doble extremo (Deque — Double-Ended Queue).
 * Permite inserción y eliminación en ambos extremos en O(1).
 * Acepta cualquier valor: primitivos u objetos.
 * Generaliza Stack y Queue — puede comportarse como cualquiera de las dos.
 * @template T - El tipo de los valores almacenados en el deque.
 */
export class Deque<T> {
  readonly #list = new DoublyLinkedList<T>()

  /**
   * Agrega un valor al final.
   * @param value - El valor a agregar (primitivo u objeto).
   */
  pushBack(value: T): void {
    this.#list.push(value)
  }

  /**
   * Agrega un valor al frente.
   * @param value - El valor a agregar (primitivo u objeto).
   */
  pushFront(value: T): void {
    this.#list.unshift(value)
  }

  /**
   * Elimina y retorna el valor del final.
   * @returns El valor eliminado, o undefined si el deque está vacío.
   */
  popBack(): T | undefined {
    return this.#list.pop()
  }

  /**
   * Elimina y retorna el valor del frente.
   * @returns El valor eliminado, o undefined si el deque está vacío.
   */
  popFront(): T | undefined {
    return this.#list.shift()
  }

  /**
   * Retorna el valor del final sin eliminarlo.
   * @returns El valor del final, o undefined si el deque está vacío.
   */
  peekBack(): T | undefined {
    return this.#list.tail?.value
  }

  /**
   * Retorna el valor del frente sin eliminarlo.
   * @returns El valor del frente, o undefined si el deque está vacío.
   */
  peekFront(): T | undefined {
    return this.#list.head?.value
  }

  /**
   * Indica si el deque está vacío.
   */
  isEmpty(): boolean {
    return this.#list.isEmpty()
  }

  /**
   * Cantidad de elementos en el deque.
   */
  get size(): number {
    return this.#list.size
  }

  /**
   * Retorna un array con todos los valores del deque, de frente a fondo.
   */
  toArray(): T[] {
    return this.#list.toArray()
  }

  /**
   * Vacía el deque completo.
   */
  clear(): void {
    this.#list.clear()
  }

  /**
   * Hace el deque iterable con for...of, spread y destructuring.
   */
  *[Symbol.iterator](): Iterator<T> {
    yield* this.#list
  }
}